import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiKnowledgeService } from '../../ai-knowledge.service';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxySnackbarModule } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';
import { ApplicationSelectorComponent } from '../../application-selector/application-selector.component';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { ACCOUNT_GROUP_ID_TOKEN } from '../../tokens';
import { KnowledgeSourceWithSuggestedFlag, WebChatWidgetAppType } from '../../ai-knowledge-util';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { ConversationApiService } from '@vendasta/conversation';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';

import { KnowledgeSourceConfig, KnowledgeSourceKey } from '@vendasta/embeddings';
import { KnowledgeCreatedEvent, StepChangeEvent } from '../common/interfaces';

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  config?: KnowledgeSourceConfig;
  isSuggested?: boolean;
  stepOption?: string;
  excludeAddToAIStep?: boolean;
  currentAppId?: string;
}

@Component({
  selector: 'ai-knowledge-business-profile',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTabsModule,
    MatSelectModule,
    MatOptionModule,
    MatExpansionModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    RouterModule,
    GalaxySnackbarModule,
    MatInputModule,
    MatStepperModule,
    MatIconModule,
    GalaxyTooltipModule,
    ApplicationSelectorComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './business-profile.component.html',
  styleUrls: ['./business-profile.component.scss'],
})
export class BusinessProfileComponent implements OnInit, KnowledgeCreatedEvent, StepChangeEvent {
  @ViewChild('applicationSelector') applicationSelector: ApplicationSelectorComponent;

  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly conversationApi = inject(ConversationApiService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly isLoading = signal(true);

  knowledgeSource: KnowledgeSourceWithSuggestedFlag;
  knowledgeCreated = new EventEmitter<string>();
  stepChange = new EventEmitter<string>();
  readonly otherAIDisclaimer = signal('');

  constructor(
    readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<BusinessProfileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {}

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;

    if (this.data?.knowledgeSourceKey) {
      await this.knowledgeService
        .getKnowledgeSource(this.data.knowledgeSourceKey.knowledgeSourceId, this.data.knowledgeSourceKey.namespace)
        .then(
          (knowledgeSource) => {
            this.knowledgeSource = knowledgeSource as KnowledgeSourceWithSuggestedFlag;
          },
          (_) => {
            this.knowledgeSource = null;
          },
        );

      this.otherAIDisclaimer.set(
        await this.knowledgeService.getOtherAIDisclaimerText(
          this.data.knowledgeSourceKey.knowledgeSourceId,
          this.data.currentAppId,
        ),
      );

      if (!this.knowledgeSource) {
        this.knowledgeSource = this.knowledgeService.buildBusinessProfileKnowledgeSource(this.data.isSuggested);
        this.cdr.detectChanges();
      }
    }
    this.isLoading.set(false);
  }

  async submit(): Promise<void> {
    if (!this.knowledgeSource) {
      await this.knowledgeService.createKnowledgeSource('Business Profile', {
        businessProfileConfig: {
          accountGroupId: this.accountGroupId(),
        },
      });
    }
    await this.applicationSelector.setAppsForKnowledgeSource();
    this.close();
  }

  hasUnsavedChanges(): boolean {
    return this.applicationSelector?.hasUnsavedChanges;
  }

  shouldSubmit(): boolean {
    return this.hasUnsavedChanges() || !this.knowledgeSource || this.applicationSelector?.forceSubmit;
  }

  customButton = {
    text: 'BUSINESS_PROFILE.MANAGE_BUSINESS_PROFILE',
    link: this.knowledgeService.buildBusinessProfileUrl(),
  };

  async createWebChatWidget(): Promise<void> {
    this.applicationSelector?.isCreatingWidget.set(true);
    const resp = await firstValueFrom(
      this.conversationApi
        .createWidget({
          name: 'Web Chat',
          namespace: this.knowledgeService.accountGroupId() || this.knowledgeService.partnerId(),
        })
        .pipe(
          catchError((err) => {
            this.confirmationModal
              .openModal({
                title: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.ERROR.TITLE'),
                message: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.ERROR.MESSAGE'),
                confirmButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.ERROR.YES'),
                cancelButtonText: this.translateService.instant('COMMON.NO'),
              })
              .subscribe((confirmed) => {
                this.applicationSelector?.isCreatingWidget.set(false);
                if (confirmed) {
                  this.createWebChatWidget();
                }
              });
            return throwError(() => err);
          }),
        ),
    );

    const widgetId = resp.widget.widgetId;
    const configurationUrl = await this.buildAiKnowledgeAppConfigurationUrl(widgetId);
    await this.knowledgeService
      .upsertKnowledgeForApp(this.buildAppId(widgetId), resp.widget.name, configurationUrl, WebChatWidgetAppType, [
        {
          id: this.knowledgeSource?.id,
          namespace: this.knowledgeSource?.namespace,
        },
      ])
      .catch(
        // ignore error here for now because it could orphan the widget otherwise
        () => console.warn('Failed to save knowledge for widget'),
      );
    this.confirmationModal
      .openModal({
        title: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.SUCCESS.TITLE'),
        confirmButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.SUCCESS.GO_TO_WEB_CHAT_SETTINGS'),
        cancelButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.SUCCESS.KEEP_EDITING'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close(true);
          this.router.navigate(this.knowledgeService.buildWebChatWidgetEditRoute(widgetId));
        } else {
          this.applicationSelector.loadApps();
          this.applicationSelector?.isCreatingWidget.set(false);
        }
      });
  }

  async buildAiKnowledgeAppConfigurationUrl(widgetId: string): Promise<string> {
    if (this.knowledgeService.accountGroupId()) {
      return `/account/location/${this.knowledgeService.accountGroupId()}/settings/widgets/${widgetId}/edit`;
    }
    return `/(inbox:inbox/widgets/${widgetId}/edit)`;
  }

  buildAppId(widgetId: string): string {
    return `APP-WEBCHAT-${widgetId}`;
  }

  getTitle(): string {
    if (!this.data?.knowledgeSourceKey) {
      return 'ADD_KNOWLEDGE';
    }
    return 'EDIT_KNOWLEDGE.TITLE';
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
        message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.dialogRef.close();
    });
  }
}
